import { defineStore } from 'pinia';
import { useAuthUserStore } from '../user';
import { isUserAuthorized } from '@/app/stores/user/types';
import {
  isSearchOperationHistoryInputType,
  mapPaymentByCriteria,
  OperationHistory,
  PaymentByCriteriaParams,
  searchPaymentByCriteria,
  SearchPaymentByCriteriaParams,
} from '@/shared/services/payment';
import {
  OperationHistoryByCriteriaFragment,
  PageInput,
  SearchOperationHistoryInput,
  Status,
} from '@/app/graphql/payment/sdk.payment';
import {
  FilterInput,
  HISTORY_FILTER_STATUS,
  HistoryTransactionState,
  INITIAL_FILTER_INPUT,
  pageableHistory,
} from './types';
import { formatDateIso } from '@/shared/lib/date';
import useObject from '@/shared/lib/use-object';
import dayjs from 'dayjs';
import { getOperationsTimeRule } from '@/shared/services/rule-engine';
import { useKeyAvailability } from '@/shared/lib/use-key-availability';
import { useFilterStatus } from '@/shared/lib/use-filter-status';
import { useFilterOperations } from '@/shared/lib/use-filter-operations';

export const useHistoryTransactionStore = defineStore('historyPayment', {
  state: (): HistoryTransactionState => ({
    filter: { ...INITIAL_FILTER_INPUT },
    transactions: {
      amountPages: 0,
      amountElements: 0,
      currentPage: 0,
      list: [],
    },
    transactionTimeRule: null,
    paramsPageable: { ...pageableHistory },
    transactionLoading: true,
    transactionError: false,
    transactionKeyAvailability: {
      available: false,
      nonAvailableFor: [],
      orgReferenceId: 0,
    },
    sendPayments: {
      count: 0,
      countProcessed: 0,
      isShowDialog: false,
      text: '',
      isSendToBank: false,
    },
  }),
  getters: {
    paramsStatus: (state) => state.filter.status,
    paramsFilter: (state) => {
      const { selectedUserContract, user } = useAuthUserStore();
      if (!selectedUserContract?.orgReferenceId) return;

      const { status, operation, ...filter } = state.filter;
      const activeStatus = useFilterStatus().getStatusByKey(status);
      const creator = activeStatus.includes(Status.Draft) ? user.userReferenceId : null;
      const operations = useFilterOperations().getOperations(operation);

      const params = {
        ...filter,
        creator,
        operations,
        account: state.filter.account ?? null,
        status: activeStatus,
        currency: state.filter.currency ? state.filter.currency?.map((item) => item.isoNum) : [],
        startAmount: state.filter?.startAmount ? state.filter.startAmount : null,
        recipientName: state.filter?.recipientName ? state.filter.recipientName : null,
        endAmount: state.filter?.endAmount ? state.filter.endAmount : null,
        startDate: state.filter?.startDate ? formatDateIso(state.filter.startDate) : null,
        endDate: state.filter?.endDate ? formatDateIso(state.filter.endDate) : null,
        origin: state.filter?.origin ? state.filter?.origin : null,
        amountAsc: state.filter.amountAsc,
        legalPartyId: selectedUserContract.orgReferenceId,
      };
      return useObject().removeEmpty(params);
    },
    hasFilterParams: (state) => {
      return (
        !!state.filter.transferNum ||
        !!state.filter.operation ||
        !!state.filter.startDate ||
        !!state.filter.endDate ||
        !!state.filter.startAmount ||
        !!state.filter.endAmount ||
        !!state.filter.currency ||
        !!state.filter.origin
      );
    },
    hasFilter: (state) => {
      return (
        state.filter.status !== HISTORY_FILTER_STATUS.ALL ||
        !!state.filter.operation ||
        !!state.filter.currency ||
        !!state.filter.transferNum ||
        !!state.filter.startDate ||
        !!state.filter.endDate ||
        !!state.filter.startAmount ||
        !!state.filter.endAmount ||
        !!state.filter.origin
      );
    },
    hasHistory: (state) => state.transactions?.list.length,
  },
  actions: {
    reset() {
      this.$reset();
    },
    async getTransactionTimeRule() {
      if (this.transactionTimeRule) return;
      this.transactionTimeRule = await getOperationsTimeRule();
    },
    async getKeyAvailability() {
      this.transactionKeyAvailability = await useKeyAvailability().fetchKeyAvailability();
    },
    async getTransaction(params: SearchOperationHistoryInput, pageable: PageInput) {
      const startStatus = this.filter.status;
      try {
        this.transactionError = false;
        const { user } = useAuthUserStore();
        if (!isUserAuthorized(user)) return;
        this.transactionLoading = true;
        const searchParams: SearchPaymentByCriteriaParams = {
          searchOperationHistory: params,
          pageable,
          user,
        };
        const response = await searchPaymentByCriteria(searchParams);
        if (startStatus === this.filter.status) this.transactions = response;
      } catch (e) {
        if (startStatus === this.filter.status) this.transactionError = true;
      } finally {
        if (startStatus === this.filter.status) this.transactionLoading = false;
      }
    },
    async getHistory() {
      this.transactionError = false;
      const params = this.paramsFilter;
      const { user } = useAuthUserStore();
      if (!isUserAuthorized(user)) return;
      if (!params || !isSearchOperationHistoryInputType(params)) return;
      await this.getTransaction(params, { ...pageableHistory });
    },
    async onLoad(done: Function) {
      if (this.transactionError || this.transactions.amountPages <= this.paramsPageable.currentPage) return done(true);

      const { selectedUserContract, user } = useAuthUserStore();
      if (!selectedUserContract?.orgReferenceId) return;

      this.paramsPageable.currentPage = this.paramsPageable.currentPage + 1;
      const params = this.paramsFilter;
      if (!params || !isUserAuthorized(user) || !isSearchOperationHistoryInputType(params)) return;

      const list = this.transactions.list ?? [];
      const searchParams: SearchPaymentByCriteriaParams = {
        searchOperationHistory: params,
        pageable: this.paramsPageable,
        user,
        transactions: list,
      };
      const res = await searchPaymentByCriteria(searchParams);
      if (res && res.list) {
        this.transactions.list.push(...res.list);
        return done();
      }

      return done(true);
    },
    setFilter(payload: FilterInput) {
      this.filter = payload;
    },
    setFilterStatus(value: HISTORY_FILTER_STATUS) {
      this.filter.status = value;
    },
    clearHistoryTransaction() {
      this.filter = { ...INITIAL_FILTER_INPUT };
    },
    resetFilter() {
      if (!this.hasFilterParams) return;
      if (!this.filter.account) {
        this.filter = { ...INITIAL_FILTER_INPUT, status: this.filter.status };
        return;
      }
      this.filter = {
        ...INITIAL_FILTER_INPUT,
        status: this.filter.status,
        account: this.filter.account,
        currency: this.filter.currency ?? null,
      };
    },
    checkPaymentFilterValid(payment: OperationHistoryByCriteriaFragment) {
      const { selectedUserContract, user } = useAuthUserStore();
      if (!selectedUserContract?.orgReferenceId) return;
      if (!this.hasFilter) return true;
      const excludeStatuses = [
        Status.OnVerification,
        Status.PartiallyConfirmed,
        Status.FirstSignInPayControlComplete,
        Status.SecondSignInPayControlComplete,
        Status.Error,
        'IMPORT',
      ];
      const activeStatus = useFilterStatus().getStatusByKey(this.filter.status);
      const creator =
        activeStatus.filter((value) => !excludeStatuses.includes(value)).length &&
        this.filter.status !== HISTORY_FILTER_STATUS.ALL
          ? user.userReferenceId
          : null;
      if (activeStatus && !activeStatus.includes(payment.statusCode)) return false;
      if (creator && creator !== +payment.createdBy) return false;
      if (this.filter.transferNum && payment.transferNum.indexOf(this.filter.transferNum) === -1) return false;
      if (this.filter.operation && this.filter.operation !== payment.operationCode) return false;
      if (this.filter.startAmount && payment.amount < this.filter.startAmount) return false;
      if (this.filter.endAmount && payment.amount > this.filter.endAmount) return false;
      if (this.filter.startDate && dayjs(formatDateIso(this.filter.startDate)).unix() > dayjs(payment.valueDate).unix())
        return false;
      if (this.filter.endDate && dayjs(formatDateIso(this.filter.endDate)).unix() < dayjs(payment.valueDate).unix())
        return false;
      return !(this.filter.currency?.length && !this.filter.currency.some((c) => c.isoCode === payment.currency));
    },
    setPayment(payment: OperationHistory) {
      const { user } = useAuthUserStore();
      if (!isUserAuthorized(user) || this.filter.amountAsc !== null || !this.transactionTimeRule) return;
      const isPaymentFilterValid = this.checkPaymentFilterValid(payment);
      if (!isPaymentFilterValid) return;

      const index = this.transactions.list.findIndex((transaction) => transaction.id === payment.id);
      if (index !== -1) return;

      const payments = {
        ...this.transactions,
        list: [payment],
      };
      const payload: PaymentByCriteriaParams = {
        payments,
        user,
      };
      const result = mapPaymentByCriteria(payload);
      this.transactions.list.unshift(...result.list);
    },
    updatePayment(payment: OperationHistory) {
      const index = this.transactions.list.findIndex((transaction) => transaction.id === payment.id);
      if (index === -1) return this.setPayment(payment);
      this.checkPaymentFilterValid(payment)
        ? (this.transactions.list[index] = payment)
        : this.transactions.list.splice(index, 1);
    },
    deletePayment(payment: OperationHistory) {
      const index = this.transactions.list.findIndex((transaction) => transaction.id === payment.id);
      if (index !== -1) this.transactions.list.splice(index, 1);
    },
  },
});
